import posthog from 'posthog-js'

import { CONFIG } from '../../Config/config'
import store from 'Config/store'

import { localStore } from '../../Module/Browser/LocalStorage'
import { isClientRole, isManagerRole } from '../../Utils/Validators/roles'

import {
  setAPIKey as setWsAPIKey,
  clearAPIKey as clearWsAPIKey,
  release as releaseWs,
} from '../../shared/websockets'
import { PATHS } from '../../Config/Routing/paths'
import { getReadableRoleByUser } from '../../Containers/UserManagement/UserDetails/utils'
class Authorization {
  /**
   * Browser storage
   */
  storage

  /**
   *
   * @param storage
   */
  constructor(storage) {
    this.storage = storage
  }

  /**
   * Login user to app
   * @param user
   * @param apiKey
   * @param countryName
   * @param countryCode
   */
  login({ user, apiKey, countryName, countryCode }) {
    this.storage.setData(CONFIG.STORE.USER_KEY, {
      ...user,
      countryName: countryName,
      countryCode: countryCode,
    })
    this.storage.setData(CONFIG.STORE.API_KEY, apiKey)

    setWsAPIKey(apiKey)
  }

  /**
   * Logout user from app
   */
  logout() {
    try {
      posthog.reset()
      this.storage.removeData(CONFIG.STORE.USER_KEY)
      this.storage.removeData(CONFIG.STORE.API_KEY)
      this.storage.removeData(CONFIG.STORE.CHAT_AUTO_OPEN)
      this.storage.removeData(CONFIG.STORE.ONBOARDING_ASSISTANT_ACTIVE_TAB)
      this.storage.removeData(CONFIG.STORE.ONBOARDING_ASSISTANT_AUTO_OPEN)
      if (store.getState().websockets.connected) {
        clearWsAPIKey()
        releaseWs()
      }
      this.destroyPromofyAuthCookie()
    } catch (error) {
      console.error(error)
    }
  }

  /**
   * Is user logged
   * @returns boolean
   */
  isLogged() {
    const user = this.getUser()
    const apiKey = this.getAPIKey()
    const authCookie = this.getAuthCookie()

    const isAllowedRoutes = [
      PATHS.main.errors,
      PATHS.main.login,
      PATHS.main.signUp,
      PATHS.main.resetPassSuccess,
    ].includes(window.location.pathname)
    if (!authCookie && !!apiKey & !isAllowedRoutes) {
      // eslint-disable-next-line no-console
      console.log('## debug, isLogged, redirect to /errors?type=auth', {
        user,
        apiKey,
        authCookie,
      })
      window.location.href = '/errors?type=auth'
    }
    return user && apiKey && authCookie
  }

  /**
   * User is admin
   * @returns boolean
   */
  isAdmin() {
    const user = this.getUser()
    return this.isLogged() && user.role === CONFIG.USER_ROLE.ADMIN
  }

  /**
   * User is city manager
   * @returns boolean
   */
  isCityManager() {
    const user = this.getUser()
    return this.isLogged() && isManagerRole(user.role)
  }

  isCityManagerAssistant() {
    const user = this.getUser()

    return (
      this.isLogged() &&
      getReadableRoleByUser(user) === CONFIG.USER_SUBROLE.CITY_MANAGER_ASSISTANT
    )
  }

  isCityManagerLegacy() {
    const user = this.getUser()
    return this.isLogged() && user.role === CONFIG.USER_ROLE.CITY_MANAGER
  }

  /**
   * User is client
   * @param role string In case that need to check before save the user role
   * @returns boolean
   */
  isClient() {
    const user = this.getUser()
    return this.isLogged() && isClientRole(user.role)
  }

  /**
   * User is client admin
   * @param role string In case that need to check before save the user role
   * @returns boolean
   */
  isClientAdmin() {
    const user = this.getUser()
    return this.isLogged() && user.role === CONFIG.USER_ROLE.CLIENT_ADMIN
  }

  /**
   * User is Client Admin and Client has Self Serve enabled
   * @returns boolean
   */
  isSelfServeEnabled() {
    const user = this.getUser()
    return (
      this.isLogged() &&
      (user.role === CONFIG.USER_ROLE.CLIENT_ADMIN ||
        user.role === CONFIG.USER_ROLE.CLIENT) &&
      user.client.isSelfServeEnabled
    )
  }

  isApiIntegrationEnabled() {
    const user = this.getUser()
    return this.isLogged() && user.client && user.client.apiAccessEnabled
  }

  /**
   * User is client role
   * @returns boolean
   */
  isClientRole() {
    return this.isClientAdmin() || this.isClient()
  }

  /**
   * User is Client in USA
   * @returns boolean
   */
  isClientInUSAorCA() {
    const user = this.getUser()
    return (
      this.isLogged() &&
      [CONFIG.USER_ROLE.CLIENT_ADMIN, CONFIG.USER_ROLE.CLIENT].indexOf(
        user.role,
      ) !== -1 &&
      (user.countryCode === 'US' || user.countryCode === 'CA')
    )
  }

  /**
   * User is recruiter
   * @param role string In case that need to check before save the user role
   * @returns boolean
   */
  isRecruiter() {
    const user = this.getUser()
    return this.isLogged() && user.role === CONFIG.USER_ROLE.RECRUITER
  }

  /**
   * User is spy
   * @param role string In case that need to check before save the user role
   * @returns boolean
   */
  isSpy() {
    const user = this.getUser()
    return this.isLogged() && user.role === CONFIG.USER_ROLE.SPY
  }
  /**
   * Get user
   */
  getUser() {
    return this.storage.getData(CONFIG.STORE.USER_KEY)
  }

  /**
   * Update user
   */
  updateUser(user) {
    this.storage.setData(CONFIG.STORE.USER_KEY, user)
  }

  /**
   * Get API key
   */
  getAPIKey() {
    return this.storage.getData(CONFIG.STORE.API_KEY)
  }

  setXeroTokens({ xeroAccessToken, xeroRefreshToken }) {
    this.storage.setData('xeroAccessToken', xeroAccessToken)
    this.storage.setData('xeroRefreshToken', xeroRefreshToken)
  }

  getXeroTokens() {
    const xeroAccessToken = this.storage.getData('xeroAccessToken')
    const xeroRefreshToken = this.storage.getData('xeroRefreshToken')

    return { xeroAccessToken, xeroRefreshToken }
  }

  setXeroTenantId(tenantId) {
    this.storage.setData('xeroTenantId', tenantId)
  }

  getXeroTenantId() {
    const tenantId = this.storage.getData('xeroTenantId')

    return tenantId
  }
  getAuthCookie() {
    const cookies = document.cookie
      .split(';')
      .reduce((cookies, cookieString) => {
        if (cookieString) {
          const [keyPart, ...valueParts] = cookieString.split('=')
          const key = keyPart ? keyPart.trim() : ''
          const value = valueParts.length > 0 ? valueParts.join('=').trim() : ''
          if (key) {
            cookies[key] = value
          }
        }
        return cookies
      }, {})
    return cookies['oppizi_' + this.getAuthCookieKey()]
  }
  getAuthCookieKey() {
    return CONFIG.ENVIRONMENT
  }
  destroyPromofyAuthCookie() {
    const environment = CONFIG.ENVIRONMENT
    const isDev = environment === 'dev'
    const isPromofy = environment === 'promofy'
    const domain = isDev ? '' : isPromofy ? '.promofy.marketing' : '.oppizi.com'

    const cookieOptions = [
      `oppizi_${this.getAuthCookieKey()}=`,
      'expires=Thu, 01 Jan 1970 00:00:00 UTC',
      'path=/',
      domain && `domain=${domain}`,
      !isDev && 'secure=true',
      !isDev && 'sameSite=none',
    ]
      .filter(Boolean)
      .join('; ')

    document.cookie = cookieOptions
  }
}

export const authorization = new Authorization(localStore)
